import { useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { formatNumber } from '../utils'
import type { PerformanceColaboradorLinha } from '../dominios/performance/tipos/performance'

const COR_ESPERADAS = '#94a3b8'
const COR_CLOCKIFY = '#465fff'
const COR_FATURADAS = '#059669'

function fmtHoras(v: number) {
  return `${formatNumber(v)} h`
}

type Props = {
  pessoaId: number
  nomeExibicao: string
  performancePorPessoa: Map<number, PerformanceColaboradorLinha>
  children?: ReactNode
}

/**
 * Hover no nome do colaborador: mesmo padrão visual do gráfico de Performance (H. esperadas, Clockify, faturadas).
 * Usa posição fixed + portal para não ser cortado por áreas com overflow.
 */
export function HoverTooltipNomeColaborador({
  pessoaId,
  nomeExibicao,
  performancePorPessoa,
  children,
}: Props) {
  const linha = performancePorPessoa.get(pessoaId)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ x: 0, y: 0 })

  const onEnter = (e: React.MouseEvent) => {
    setCoords({ x: e.clientX + 10, y: e.clientY + 8 })
    setOpen(true)
  }
  const onMove = (e: React.MouseEvent) => {
    if (open)
      setCoords({ x: e.clientX + 10, y: e.clientY + 8 })
  }
  const onLeave = () => setOpen(false)

  const nomeTitulo = linha?.nome?.trim() ? linha.nome : nomeExibicao

  return (
    <>
      <span
        className="cursor-default rounded px-0.5 transition-colors hover:bg-gray-100"
        onMouseEnter={onEnter}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        {children ?? nomeExibicao}
      </span>
      {open &&
        createPortal(
          <div
            className="pointer-events-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-theme-sm max-w-md"
            style={{ position: 'fixed', left: coords.x, top: coords.y, zIndex: 9999 }}
          >
            <p className="font-medium text-gray-900">{nomeTitulo}</p>
            {linha?.nomesTickets && linha.nomesTickets.length > 0 && (
              <p className="mt-1 text-xs leading-relaxed text-gray-600">
                <span className="font-medium text-gray-700">Tickets: </span>
                {linha.nomesTickets.join(' · ')}
              </p>
            )}
            <ul className="mt-2 space-y-0.5 text-xs">
              <li className="flex justify-between gap-6">
                <span style={{ color: COR_ESPERADAS }}>H. esperadas</span>
                <span className="font-medium text-gray-800">
                  {linha != null ? fmtHoras(linha.horasEsperadas) : '—'}
                </span>
              </li>
              <li className="flex justify-between gap-6">
                <span style={{ color: COR_CLOCKIFY }}>H. Clockify</span>
                <span className="font-medium text-gray-800">
                  {linha != null ? fmtHoras(linha.horasTrabalhadas) : '—'}
                </span>
              </li>
              <li className="flex justify-between gap-6">
                <span style={{ color: COR_FATURADAS }}>H. faturadas</span>
                <span className="font-medium text-gray-800">
                  {linha != null ? fmtHoras(linha.horasFaturadas) : '—'}
                </span>
              </li>
            </ul>
          </div>,
          document.body,
        )}
    </>
  )
}

type IntegrantesProps = {
  /** Nome do time (cabeçalho do card). */
  titulo: string
  /** Nomes dos integrantes (texto livre, ex.: do cadastro do time). */
  textoIntegrantes: string
  children: ReactNode
}

/**
 * Hover no nome do time: card branco alinhado ao de Performance (sem cursor "?").
 */
export function HoverTooltipIntegrantesTime({ titulo, textoIntegrantes, children }: IntegrantesProps) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ x: 0, y: 0 })

  const onEnter = (e: React.MouseEvent) => {
    setCoords({ x: e.clientX + 10, y: e.clientY + 8 })
    setOpen(true)
  }
  const onMove = (e: React.MouseEvent) => {
    if (open)
      setCoords({ x: e.clientX + 10, y: e.clientY + 8 })
  }
  const onLeave = () => setOpen(false)

  return (
    <>
      <span
        className="cursor-default rounded px-0.5 transition-colors hover:bg-gray-100"
        onMouseEnter={onEnter}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div
            className="pointer-events-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-theme-sm max-w-md"
            style={{ position: 'fixed', left: coords.x, top: coords.y, zIndex: 9999 }}
          >
            <p className="font-medium text-gray-900">{titulo}</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              <span className="font-medium text-gray-700">Integrantes: </span>
              {textoIntegrantes}
            </p>
          </div>,
          document.body,
        )}
    </>
  )
}
